import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthStore } from '@core/service/auth-store.service';
import {
  AnexoCandidaturaDto,
  CandidatoDocumentoDto,
  EstadoValidacaoDocumentoEnum,
  ValidacaoDocumentosResultadoDto,
  ValidarCandidatoDocumentoItemDto,
} from './documentos-candidatura.models';

interface CandidaturaDocumentoDto {
  id?: string;
  candidaturaId?: string;
  candidatoDocumentoId?: string;
  candidatoDocumento?: CandidatoDocumentoDto;
  candidaturaRequisito?: {
    requisito?: {
      tipoDocumentoId?: string;
      tipoDocumento?: { id?: string; codigo?: string; nome?: string };
    };
  };
  estadoValidacaoEnum?: number;
  estadoValidacao?: string;
  validado?: boolean;
  dataValidacao?: string | null;
  validadoPor?: string | null;
  motivoRejeicao?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ValidacaoDocumentosService {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(AuthStore);

  // Endpoint base da API Concurso (ex: https://bdp-concurso-api.dev.olualinet.ao/api/v1)
  private readonly baseUrl = environment.apiConcursoUrl.replace(/\/+$/, '');

  /**
   * Obtém os documentos associados à candidatura com o respectivo estado de validação.
   * Rota: GET /api/v1/candidatura-documentos?CandidaturaId={id}
   */
  listarAnexos(candidaturaId: string): Observable<AnexoCandidaturaDto[]> {
    return this.consultarCandidaturaDocumentos({
      candidaturaId,
      pageNumber: 1,
      pageSize: 100,
    }).pipe(
      map((response) =>
        this.extrairDocumentos(response)
          .filter((documento) => String(documento.candidaturaId ?? '') === candidaturaId)
          .map((documento) => this.normalizarDocumentoCandidatura(documento)),
      ),
    );
  }

  /**
   * Consulta documentos de candidatura com filtros e paginação.
   * Rota: GET /api/v1/candidatura-documentos
   */
  consultarCandidaturaDocumentos(filtros: {
    candidaturaId?: string;
    concursoId?: string;
    candidatoId?: string;
    candidatoDocumentoId?: string;
    estadoValidacao?: string;
    pageNumber?: number;
    pageSize?: number;
    searchTerm?: string;
  }): Observable<unknown> {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        const paramKey = key.charAt(0).toUpperCase() + key.slice(1);
        params = params.set(paramKey, String(val));
      }
    });

    return this.http.get(`${this.baseUrl}/candidatura-documentos`, { params });
  }

  /**
   * Função central de normalização para validação de documentos.
   * REGRA CRÍTICA:
   * - Ao reprovar: motivo é obrigatório (trim, mínimo 10 caracteres). Envia motivoRejeicao preenchido.
   * - Em QUALQUER outro estado: motivoRejeicao DEVE SER null (nunca string vazia).
   */
  normalizarValidacao(
    estado: 'APROVADO' | 'REPROVADO' | EstadoValidacaoDocumentoEnum | string | number,
    motivo?: string | null,
  ): { estadoValidacaoEnum: EstadoValidacaoDocumentoEnum; motivoRejeicao: string | null } {
    const isReprovado =
      estado === EstadoValidacaoDocumentoEnum.REJEITADO ||
      estado === 3 ||
      String(estado).toUpperCase() === 'REPROVADO' ||
      String(estado).toUpperCase() === 'REJEITADO';

    const motivoNormalizado = (motivo ?? '').trim();

    if (isReprovado) {
      if (!motivoNormalizado) {
        throw new Error('O motivo de rejeição é obrigatório.');
      }
      if (motivoNormalizado.length < 10) {
        throw new Error('O motivo de rejeição deve ter pelo menos 10 caracteres.');
      }
      return {
        estadoValidacaoEnum: EstadoValidacaoDocumentoEnum.REJEITADO,
        motivoRejeicao: motivoNormalizado,
      };
    }

    const isEmValidacao =
      estado === EstadoValidacaoDocumentoEnum.EM_VALIDACAO ||
      estado === 1 ||
      String(estado).toUpperCase() === 'EM_VALIDACAO';

    if (isEmValidacao) {
      return {
        estadoValidacaoEnum: EstadoValidacaoDocumentoEnum.EM_VALIDACAO,
        motivoRejeicao: null,
      };
    }

    return {
      estadoValidacaoEnum: EstadoValidacaoDocumentoEnum.VALIDADO,
      motivoRejeicao: null,
    };
  }

  /**
   * Valida um ou mais documentos de uma candidatura no Concurso.
   * Rota: POST /api/v1/candidatura-documentos/validar-documentos
   * Contrato Swagger: Array de ValidarCandidatoDocumentoItemDto
   */
  validar(
    candidaturaId: string,
    itens: Array<{
      candidatoDocumentoId: string;
      estado: 'APROVADO' | 'REPROVADO' | EstadoValidacaoDocumentoEnum | string | number;
      motivo?: string | null;
    }>,
  ): Observable<ValidacaoDocumentosResultadoDto> {
    const payload: ValidarCandidatoDocumentoItemDto[] = itens.map((i) => {
      const normalizado = this.normalizarValidacao(i.estado, i.motivo);
      return {
        candidatoDocumentoId: i.candidatoDocumentoId,
        estadoValidacaoEnum: normalizado.estadoValidacaoEnum,
        motivoRejeicao: normalizado.motivoRejeicao,
      };
    });

    return this.http.post<ValidacaoDocumentosResultadoDto>(
      `${this.baseUrl}/candidatura-documentos/validar-documentos`,
      payload,
    );
  }

  /**
   * Descarrega o ficheiro como Blob com o Bearer token injetado pelo interceptor.
   */
  obterDocumentoBlob(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Verifica se o utilizador autenticado tem perfil autorizado para aprovar ou reprovar documentos.
   */
  podeValidar(): boolean {
    const roles = this.authStore.roles();
    if (!roles || roles.length === 0) {
      // Se não houver roles restritivas configuradas no authStore, permitir aos utilizadores do Admin
      return true;
    }
    const rolesAutorizadas = ['admin', 'gestor', 'analista', 'administrador', 'comissao', 'drh'];
    return this.authStore.hasAny(rolesAutorizadas);
  }

  private normalizarAnexo(anexo: AnexoCandidaturaDto): AnexoCandidaturaDto {
    const preview = anexo.urlPreview || anexo.linkAnexoPreview || (anexo.id ? `${this.baseUrl}/anexos/${anexo.id}/preview` : '');
    const candDoc = anexo.candidatoDocumento;

    const estadoEnum = candDoc?.estadoValidacaoEnum ?? anexo.estadoValidacaoEnum ?? EstadoValidacaoDocumentoEnum.EM_VALIDACAO;
    const estadoTexto = candDoc?.estadoValidacao ?? anexo.estadoValidacao ?? (estadoEnum === EstadoValidacaoDocumentoEnum.VALIDADO ? 'Validado' : (estadoEnum === EstadoValidacaoDocumentoEnum.REJEITADO ? 'Rejeitado' : 'Em Validação'));
    const motivo = candDoc?.motivoRejeicao ?? anexo.motivoRejeicao ?? null;
    const validado = candDoc?.validado ?? (estadoEnum === EstadoValidacaoDocumentoEnum.VALIDADO);
    const validadoPor = candDoc?.validadoPor ?? anexo.validadoPor ?? null;
    const dataValidacao = candDoc?.dataValidacao ?? anexo.dataValidacao ?? null;
    const docId = anexo.candidatoDocumentoId || candDoc?.id || anexo.id;

    return {
      ...anexo,
      urlPreview: preview,
      candidatoDocumentoId: docId,
      estadoValidacaoEnum: estadoEnum,
      estadoValidacao: estadoTexto,
      motivoRejeicao: motivo,
      validado,
      validadoPor,
      dataValidacao,
    };
  }

  private extrairDocumentos(response: unknown): CandidaturaDocumentoDto[] {
    if (Array.isArray(response)) return response as CandidaturaDocumentoDto[];
    if (!response || typeof response !== 'object') {
      throw new Error('A API devolveu uma resposta inválida para os documentos da candidatura.');
    }

    const body = response as { data?: unknown; object?: unknown };
    if (Array.isArray(body.data)) return body.data as CandidaturaDocumentoDto[];
    if (Array.isArray(body.object)) return body.object as CandidaturaDocumentoDto[];
    if (body.object && typeof body.object === 'object') {
      const object = body.object as { data?: unknown };
      if (Array.isArray(object.data)) return object.data as CandidaturaDocumentoDto[];
    }

    throw new Error('A resposta da API não contém a lista de documentos da candidatura.');
  }

  private normalizarDocumentoCandidatura(documento: CandidaturaDocumentoDto): AnexoCandidaturaDto {
    const candidatoDocumento = documento.candidatoDocumento;
    const anexo = candidatoDocumento?.anexo;
    if (!anexo) {
      throw new Error(`O documento de candidatura ${documento.id ?? ''} não contém o anexo associado.`);
    }

    const tipoRequisito = documento.candidaturaRequisito?.requisito;
    const candidatoDocumentoComValidacao: CandidatoDocumentoDto = {
      ...candidatoDocumento,
      tipoDocumentoId: candidatoDocumento.tipoDocumentoId ?? tipoRequisito?.tipoDocumentoId,
      tipoDocumento: candidatoDocumento.tipoDocumento ?? tipoRequisito?.tipoDocumento,
      estadoValidacaoEnum: documento.estadoValidacaoEnum ?? candidatoDocumento.estadoValidacaoEnum,
      estadoValidacao: documento.estadoValidacao ?? candidatoDocumento.estadoValidacao,
      validado: documento.validado ?? candidatoDocumento.validado,
      dataValidacao: documento.dataValidacao ?? candidatoDocumento.dataValidacao,
      validadoPor: documento.validadoPor ?? candidatoDocumento.validadoPor,
      motivoRejeicao: documento.motivoRejeicao ?? candidatoDocumento.motivoRejeicao,
    };

    return this.normalizarAnexo({
      ...anexo,
      id: anexo.id || candidatoDocumento.anexoId || documento.candidatoDocumentoId || documento.id || '',
      candidaturaDocumentoId: documento.id,
      nomeFicheiro: anexo.nomeFicheiro || 'Documento sem nome',
      mimeType: anexo.mimeType || '',
      tamanho: anexo.tamanho ?? candidatoDocumento.tamanhoBytes,
      createdAt: anexo.createdAt ?? candidatoDocumento.createdAt,
      candidatoDocumentoId: documento.candidatoDocumentoId ?? candidatoDocumento.id,
      candidatoDocumento: candidatoDocumentoComValidacao,
      tipoDocumentoId: candidatoDocumentoComValidacao.tipoDocumentoId,
      tipoDocumento: candidatoDocumentoComValidacao.tipoDocumento,
      estadoValidacaoEnum: documento.estadoValidacaoEnum,
      estadoValidacao: documento.estadoValidacao,
      validado: documento.validado,
      dataValidacao: documento.dataValidacao,
      validadoPor: documento.validadoPor,
      motivoRejeicao: documento.motivoRejeicao,
    });
  }
}
